/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Row, Col, Typography, notification, Button, Upload } from 'antd';
import { Button as CrceButton } from '@crc-evans/ui-components';
import { Upload as UploadIcon } from 'lucide-react';
import './AddNewPQTPipeline.css';
import { addSowDetails } from '../../redux/PQTsowDetails/actions';
import { addNewPilelineDetails, UpdateSowDetails } from '../../redux/PQTsowDetails/saga';
import axiosConfig from '../../redux/PQTsowDetails/axiosConfig';
import axios from 'axios';

const { Text } = Typography;
const { TextArea } = Input;
const AddNewPQTPipeline = (props) => {
  const {
    isModalVisible,
    setIsModalVisible,
    editDetailsForm,
    projectId,
    onSave,
    editValue,
    setEditValue,
    SubmittedNewPipeline,
    selectedData,
    /** Read-only pipeline details (e.g. from PQT Results “View details”). */
    readOnly = false,
    setReadOnly,
    /** False when user must not edit (guest / no write permission). */
    allowPipelineEdit = true,
  } = props;

  const [fileList, setFileList] = useState([]);
  const [disableFileUpload, setDisableFileUpload] = useState(false);
  const [Loading, setLoading] = useState(false);
  const [uploadedFilesList, setUploadedFilesList] = useState([]); // Store uploaded files
  const [formData, setFormData] = useState({
    pipelineSection: '',
    projectId: projectId,
    applicationProcedure: '',
    recordDocument: '',
    specification: '',
    weldIDPrefix: '',
    documents: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });

  useEffect(() => {
    if (!isModalVisible) return;
    const pid = projectId;
    if (editValue && selectedData && typeof selectedData === 'object') {
      setFormData((prev) => ({
        ...prev,
        ...selectedData,
        projectId: pid || selectedData.projectId || prev.projectId,
      }));
    } else if (!editValue && pid) {
      setFormData({
        pipelineSection: '',
        projectId: pid,
        applicationProcedure: '',
        recordDocument: '',
        specification: '',
        weldIDPrefix: '',
        documents: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
    }
  }, [isModalVisible, editValue, selectedData, projectId]);

  // Handle file upload changes
  const handleFileChange = ({ fileList }) => {
    setFileList(fileList);
  };

  // Upload props
  const fileProps = {
    multiple: true, // Allow multiple files
    fileList,
    beforeUpload: (file) => {
      setFileList((prev) => [...prev, file]);
      return false; // Prevent automatic upload
    },
    onRemove: (file) => {
      setFileList((prev) => prev.filter((item) => item.uid !== file.uid));
    },
    accept: '.xlsx,.xls,.pdf', // Allow Excel & PDF files
    maxCount: 100, // Allow up to 100 files
  };
  /** Validate Form Data */
  const validateForm = () => {
    let errors = [];

    if (!formData.pipelineSection.trim()) errors.push('Pipeline Section is required.');
    if (!formData.specification.trim()) errors.push('Specification is required.');
    if (!formData.applicationProcedure.trim()) errors.push('Application Procedure is required.');
    if (!formData.recordDocument.trim()) errors.push('Record Document is required.');
    if (!formData.weldIDPrefix.trim()) errors.push('Weld ID Prefix is required.');

    if (errors.length > 0) {
      notification.error({
        message: 'Validation Error',
        description: errors.join(' '),
      });
      return false;
    }

    return true;
  };

  // Function to upload files to S3
  const uploadFilesToS3 = async () => {
    try {
      const uploadedFiles = [];

      if (fileList && fileList.length > 0) {
        const fileObjs = fileList.map((file) => ({
          fileName: file.name.replace(/\(\d+\)/g, '').trim(), // Remove (1), (2), etc.
          contentType: file.type,
        }));

        // Get signed URLs for multiple files
        const response = await axiosConfig.post(`/pqt-sow/upload/presigned-url`, {
          files: fileObjs,
        });
        //  console.log(response)
        if (response.status === 200) {
          for (const data of response.data.data) {
            const file = fileList.find(
              (f) => f.name.replace(/\(\d+\)/g, '').trim() === data.fileName
            ); // Match sanitized filename
            //  console.log(data, file)
            if (file) {
              try {
                // Upload file to S3
                const uploadResponse = await axios.put(data.presignedUrl, file, {
                  headers: {
                    'Content-Disposition': 'attachment',
                    'Content-Type': file.type || 'application/octet-stream',
                  },
                });
                //  console.log(uploadResponse)

                if (uploadResponse.status === 200) {
                  uploadedFiles.push({ s3Url: data.fileUrl, fileName: data.fileName });
                } else {
                  console.error(`Error uploading file: ${file.name}`, uploadResponse);
                }
              } catch (error) {
                console.error(`Error processing file: ${file.name}`, error);
              }
            }
          }
        }

        setFileList([]); // Clear selected files after upload
        setUploadedFilesList(uploadedFiles); // Store uploaded URLs
        //console.log("Uploaded Files:", uploadedFiles);
      }
      return uploadedFiles;
    } catch (error) {
      console.error('Error uploading files:', error);
    }
  };

  /** Handle Submit */
  const handleOk = async () => {
    if (readOnly) return;
    if (!validateForm()) return;

    // console.log("Form Data Submitted:", formData);

    // API Call or Save Data
    //onSave(formData);
    setLoading(true);
    if (fileList && fileList.length > 0) {
      let documentData = formData['documents'] || []; // Ensure it's an array

      const createdDoc = await uploadFilesToS3();

      formData['documents'] = [...documentData, ...createdDoc];
    }

    if (editValue) {
      await UpdateSowDetails(formData);
    } else await addNewPilelineDetails(formData);
    // Reset Form

    setLoading(false);
    setEditValue(false);
    setReadOnly?.(false);
    SubmittedNewPipeline();
    setIsModalVisible(false);
  };

  /** Handle Input Change */
  const handleInputChange = (name, value) => {
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  /** Handle Cancel */
  const handleCancel = () => {
    setFormData({
      pipelineSection: '',
      projectId: '',
      applicationProcedure: '',
      recordDocument: '',
      specification: '',
      weldIDPrefix: '',
      documents: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
    setEditValue(false);
    setReadOnly?.(false);
    setIsModalVisible(false);
  };

  const isFieldsDisabled = readOnly;
  const modalTitle = readOnly ? 'Pipeline details' : 'Statement of Work';

  return (
    <Modal
      width="min(640px, 94vw)"
      wrapClassName={`pqt-sow-pipeline-modal heat-profile-modal${readOnly ? ' pqt-sow-pipeline-modal--readonly' : ''}`}
      className="pipeline-modal pqt-sow-pipeline-dialog"
      title={<span className="pqt-sow-pipeline-modal__title">{modalTitle}</span>}
      centered
      visible={isModalVisible}
      maskClosable={false}
      destroyOnClose
      maskStyle={{ background: 'rgba(15, 23, 42, 0.45)', backdropFilter: 'none' }}
      footer={
        <div className="pqt-sow-pipeline-modal__footer">
          {readOnly ? (
            <>
              <CrceButton label="Close" variant="tertiary" size="medium" onClick={handleCancel} />
              {allowPipelineEdit ? (
                <CrceButton
                  label="Edit pipeline"
                  variant="primary"
                  size="medium"
                  onClick={() => setReadOnly?.(false)}
                />
              ) : null}
            </>
          ) : (
            <>
              <CrceButton label="Cancel" variant="tertiary" size="medium" onClick={handleCancel} disabled={Loading} />
              <CrceButton
                label={Loading ? (editValue ? 'Updating…' : 'Adding…') : editValue ? 'Update' : 'Add'}
                variant="primary"
                size="medium"
                onClick={handleOk}
                disabled={Loading}
              />
            </>
          )}
        </div>
      }
    >
      <Form form={editDetailsForm} name="basic" layout="vertical">
        <Row gutter={24}>
          <Col lg={12}>
            <Form.Item label="Pipeline Section" required>
              <Input
                value={formData.pipelineSection}
                placeholder="Enter Pipeline Section"
                onChange={(e) => handleInputChange('pipelineSection', e.target.value)}
                size="large"
                disabled={isFieldsDisabled}
              />
            </Form.Item>
          </Col>
          <Col lg={12}>
            <Form.Item label="Specification" required>
              <Input
                value={formData.specification}
                placeholder="Enter Specification"
                onChange={(e) => handleInputChange('specification', e.target.value)}
                size="large"
                disabled={isFieldsDisabled}
              />
            </Form.Item>
          </Col>
          <Col lg={12}>
            <Form.Item label="Application Procedure" required>
              <TextArea
                value={formData.applicationProcedure}
                placeholder="Enter Application Procedure"
                onChange={(e) => handleInputChange('applicationProcedure', e.target.value)}
                size="large"
                disabled={isFieldsDisabled}
                style={{
                  height: 100, // Fixed height
                  overflowY: 'auto', // Enables scrollbar when needed
                  resize: readOnly ? 'none' : 'vertical', // Prevents manual resizing
                }}
              />
            </Form.Item>
          </Col>
          <Col lg={12}>
            <Form.Item label="Record Document" required>
              <TextArea
                value={formData.recordDocument}
                placeholder="Enter Record Document"
                onChange={(e) => handleInputChange('recordDocument', e.target.value)}
                size="large"
                disabled={isFieldsDisabled}
                style={{
                  height: 100, // Fixed height
                  overflowY: 'auto', // Enables scrollbar when needed
                  resize: readOnly ? 'none' : 'vertical', // Prevents manual resizing
                }}
              />
            </Form.Item>
          </Col>
          <Col lg={12}>
            <Form.Item label="Weld ID Prefix" required>
              <Input
                value={formData.weldIDPrefix}
                placeholder="Enter Weld ID Prefix"
                onChange={(e) => handleInputChange('weldIDPrefix', e.target.value)}
                size="large"
                disabled={isFieldsDisabled}
              />
              {formData.weldIDPrefix && (
                <div>
                  {' '}
                  <Text
                    style={{ letterSpacing: '0.5px' }}
                  >{`Ex: ${formData.weldIDPrefix} 01`}</Text>
                  <p className="pqt-sow-weld-hint">*PQT weld number will generate as shown in above example.</p>{' '}
                </div>
              )}
            </Form.Item>
          </Col>
          <Col lg={24}>
            <div className="uploadFile_admin">
              <Upload {...fileProps} disabled={isFieldsDisabled}>
                <Button
                  type="default"
                  className="pqt-sow-upload-trigger"
                  icon={<UploadIcon size={18} strokeWidth={2} aria-hidden />}
                  disabled={isFieldsDisabled || fileList.length >= 100}
                >
                  Select File (Excel / PDF)
                </Button>
              </Upload>
            </div>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default AddNewPQTPipeline;
